import { Flex, Text } from '@chakra-ui/react';
import { useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useKibanaLogger } from '../../../shared/hooks/kibana-logger/kibana-logger';
import { DEFAULT_ERROR_MSG } from '../../../shared/ui/urql-and-tanstack-error-message/urql-and-tanstack-error-message';

const DashboardErrorFallback = () => (
  <Flex
    data-testid="error-boundary"
    width="100%"
    height="100vh"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    padding={8}
    backgroundColor="var(--chakra-colors-background-primary)"
    color="var(--chakra-colors-text-primary)"
  >
    <Text fontSize="xl" fontWeight="bold" mb={4}>
      Something went wrong
    </Text>
    <Text fontSize="md" mb={6} textAlign="center">
      {DEFAULT_ERROR_MSG}
    </Text>
  </Flex>
);

export const DashboardErrorBoundary = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { kibanaLogger } = useKibanaLogger();

  const handleError = useCallback(
    (error: Error, info: { componentStack?: string | null }) => {
      console.error('DashboardErrorBoundary', error);

      if (kibanaLogger === undefined) return;

      const baseMessage = error.stack
        ? `DashboardErrorBoundary: ${error.message}\n${error.stack}`
        : `DashboardErrorBoundary: ${error.message}`;

      const fullMessage = info.componentStack
        ? `${baseMessage}\n\nComponent Stack:${info.componentStack}`
        : baseMessage;

      kibanaLogger.error(fullMessage, {
        context: 'DashboardErrorBoundary',
      });
    },
    [kibanaLogger]
  );

  return (
    <ErrorBoundary
      FallbackComponent={DashboardErrorFallback}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
};
